import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { TeamInfo } from '../TeamInfo';
import { RaceControlEntry } from '../types';
import './PenaltyModal.css';

interface PenaltyModalProps {
  team: TeamInfo | null;
  entries: RaceControlEntry[];
  onClose: () => void;
}

function flagClass(flag: string): string {
  switch (flag) {
    case 'penalty':
      return 'pm-penalty';
    case 'warning':
      return 'pm-warning';
    default:
      return 'pm-default';
  }
}

export const PenaltyModal: React.FC<PenaltyModalProps> = ({ team, entries, onClose }) => {
  useEffect(() => {
    if (!team) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [team, onClose]);

  if (!team) return null;

  const title =
    team.teamName?.trim() ||
    team.driver?.trim() ||
    `Kart ${team.kartNumber || '—'}`;

  return createPortal(
    <div
      className="penalty-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="penalty-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="penalty-modal" onClick={(e) => e.stopPropagation()}>
        <div className="penalty-modal-header">
          <div>
            <h2 id="penalty-modal-title">{title}</h2>
            <div className="penalty-modal-meta">
              Kart {team.kartNumber || '—'} · {entries.length} penalt
              {entries.length === 1 ? 'y' : 'ies'}/warning
            </div>
          </div>
          <button
            type="button"
            className="penalty-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="penalty-modal-body">
          {entries.length === 0 ? (
            <div className="penalty-modal-empty">No penalties for this team.</div>
          ) : (
            entries.map((entry, index) => (
              <div key={index} className={`pm-row ${flagClass(entry.flag)}`}>
                <span className="pm-time">{entry.time || '—'}</span>
                <span className="pm-flag">{(entry.flag || 'info').toUpperCase()}</span>
                <span className="pm-text">{entry.text}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PenaltyModal;
